/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — print job dispatch (root cause #5 fix).

   OLD bug: the iframe + `window.print()` path never told the OS print
   job which orientation to use — only jsPDF got explicit dimensions,
   so a "Landscape" pick silently printed at the printer's default
   orientation.

   NEW behavior:
   - DESKTOP (Electron, preload bridge present): renderer sends the
     composed PNG + page size + landscape flag over a typed IPC
     channel; main opens an offscreen BrowserWindow and calls
     `webContents.print({ landscape, pageSize })` — Electron plumbs
     `landscape` straight into the native Windows print job, so this
     is GUARANTEED correct regardless of what the CSS says.
   - WEB (no bridge — feature-detected exactly like `checkWordAvailable`
     elsewhere in the app): falls back to the hidden-iframe +
     `window.print()` approach, improved: explicit `@page` size (both
     the precise inch pair AND, cascaded before it so it doesn't win,
     a bare `landscape`/`portrait` keyword hint some engines read) and,
     for landscape jobs specifically, the raster is rotated 90° inside
     a portrait-shaped page container. Browsers/printer drivers that
     honor `@page` will get a correct landscape sheet either way; ones
     that silently force portrait (common for native OS dialogs) still
     get a page-filling image instead of a tiny sheet stranded in the
     corner — the user rotates the printed page by hand to read it.
     This is a best-effort improvement, not a guarantee (see brief).

   - SAVE AS PDF (desktop): TRUE VECTOR PDF, not this file's raster
     concern above — Chromium's own print engine renders the REAL sheet
     DOM (crisp paths, selectable text) via `webContents.printToPDF`.
     Renderer serializes a self-contained HTML doc of the sheet
     (`serializeSheetHtml.ts`) and hands it to `orgchart:export-pdf`; web
     keeps the unchanged jsPDF raster path (heavy PDF fidelity is
     desktop's job — see `PrintExportDialog.tsx`).
   ═══════════════════════════════════════════════════════════════ */

import type { SpecFormaApi } from '../../../../../shared/ipc-api';

export interface PrintChartImageResult {
  success: boolean;
  error?: string;
}

export interface ExportChartPdfResult {
  success: boolean;
  path?: string;
  canceled?: boolean;
  error?: string;
}

/** The preload bridge (`window.api`), typed — no ambient global declared
 *  for it elsewhere in the app, so read it via an `unknown` cast rather
 *  than `any`. */
function getApi(): Partial<SpecFormaApi> | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { api?: SpecFormaApi }).api;
}

/** True when the Electron preload bridge exposes the desktop print IPC —
 *  same feature-detect shape as `window.api?.checkWordAvailable`. */
export function hasDesktopPrintBridge(): boolean {
  return !!getApi()?.orgChartPrintImage;
}

/** True when the Electron preload bridge exposes the desktop vector-PDF
 *  export IPC. Gates `PrintExportDialog`'s "Save as PDF" between the
 *  vector path (desktop) and the raster jsPDF path (web). */
export function hasDesktopPdfBridge(): boolean {
  return !!getApi()?.orgChartExportPdf;
}

/** Exports a self-contained sheet HTML document to a TRUE VECTOR PDF via
 *  the desktop print engine. Resolves with `{ success: false, canceled:
 *  true }` if the user backs out of the native save dialog — that's not
 *  an error, callers should reset quietly. Throws only if the bridge
 *  itself is missing (caller should check `hasDesktopPdfBridge()` first). */
export async function exportChartPdf(
  html: string,
  wIn: number,
  hIn: number,
  suggestedName: string
): Promise<ExportChartPdfResult> {
  const api = getApi();
  if (!api?.orgChartExportPdf) {
    throw new Error('The desktop PDF export bridge is not available.');
  }
  return api.orgChartExportPdf({ html, widthIn: wIn, heightIn: hIn, suggestedName });
}

/** Prints a rasterized org-chart sheet. Resolves once the OS print job has
 *  been dispatched (desktop) or the print dialog has been invoked (web) —
 *  throws with a user-presentable message on failure. */
export async function printChartImage(
  dataUrl: string,
  wIn: number,
  hIn: number,
  landscape: boolean
): Promise<void> {
  const api = getApi();
  if (api?.orgChartPrintImage) {
    const result: PrintChartImageResult = await api.orgChartPrintImage({
      pngDataUrl: dataUrl,
      widthIn: wIn,
      heightIn: hIn,
      landscape,
    });
    if (!result?.success) {
      throw new Error(result?.error || 'The desktop print job failed.');
    }
    return;
  }
  printImageWeb(dataUrl, wIn, hIn, landscape);
}

/** Web fallback — hidden iframe + `window.print()`, improved per the
 *  header comment above. Fire-and-forget (a plain browser print dialog
 *  gives us no success/failure callback). */
function printImageWeb(dataUrl: string, wIn: number, hIn: number, landscape: boolean): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  const doc = iframe.contentDocument;
  if (!doc) {
    cleanup();
    return;
  }

  doc.open();
  doc.write(buildPrintHtml(dataUrl, wIn, hIn, landscape));
  doc.close();

  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(cleanup, 1500);
    }, 200);
  };
}

function buildPrintHtml(dataUrl: string, wIn: number, hIn: number, landscape: boolean): string {
  const orientationWord = landscape ? 'landscape' : 'portrait';
  if (!landscape) {
    // Portrait is the browser/printer default nearly everywhere — no
    // rotation trick needed, just the explicit page size.
    return `<!DOCTYPE html><html><head><title>Org Chart</title><style>
      @page { size: ${orientationWord}; }
      @page { size: ${wIn}in ${hIn}in; margin: 0; }
      html, body { margin: 0; padding: 0; background: #fff; }
      img { display: block; width: ${wIn}in; height: ${hIn}in; }
    </style></head><body><img src="${dataUrl}" /></body></html>`;
  }
  // Landscape: declare it explicitly for engines that honor @page, AND
  // rotate the raster 90deg inside a portrait-shaped page container so a
  // printer/driver that ignores the hint and forces its portrait default
  // still gets a page-filling image (physically rotate the printout to
  // read it) instead of a tiny sheet marooned on a mostly-blank page.
  return `<!DOCTYPE html><html><head><title>Org Chart</title><style>
    @page { size: ${orientationWord}; }
    @page { size: ${wIn}in ${hIn}in landscape; margin: 0; }
    html, body { margin: 0; padding: 0; background: #fff; overflow: hidden; }
    .rot { width: ${hIn}in; height: ${wIn}in; position: relative; overflow: hidden; }
    .rot img {
      position: absolute;
      top: 50%;
      left: 50%;
      width: ${wIn}in;
      height: ${hIn}in;
      transform: translate(-50%, -50%) rotate(90deg);
      transform-origin: center center;
    }
  </style></head><body><div class="rot"><img src="${dataUrl}" /></div></body></html>`;
}
